import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import '../styles/nassar-theme.css';

const DashboardLayout = () => {
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        // Decode JWT token to get user info
        const token = localStorage.getItem('nassar_token');
        if (token) {
            try {
                // Decode the payload (second part of JWT)
                const payload = token.split('.')[1];
                const decodedPayload = JSON.parse(atob(payload));
                setUser({
                    id: decodedPayload.id,
                    name: decodedPayload.name,
                    type: decodedPayload.type
                });
            } catch (error) {
                console.error('Error decoding token:', error);
            }
        }
    }, []);

    const handleLogout = () => {
        // Mark this as a manual logout to prevent showing session expired message
        sessionStorage.setItem('manual_logout', 'true');
        // Clear any existing session expired message
        sessionStorage.removeItem('session_expired_message');
        localStorage.removeItem('nassar_token');
        // Use window.location.href for immediate redirect to prevent any pending requests
        window.location.href = '/';
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    return (
        <div className="dashboard-container">
            {/* Mobile Menu Toggle */}
            <button className="mobile-menu-toggle" onClick={toggleSidebar}>
                <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
            </button>

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 999,
                        display: window.innerWidth <= 768 ? 'block' : 'none'
                    }}
                    onClick={closeSidebar}
                />
            )}

            {/* Sidebar */}
            <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <div style={{
                        width: '98px',
                        height: '98px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        margin: '0 auto 0.75rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 3px 9px rgba(0, 0, 0, 0.15)'
                    }}>
                        <img 
                            src={`${process.env.PUBLIC_URL}/nassar_logo.png`}
                            alt="نصّار" 
                            className="sidebar-logo"
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover',
                                objectPosition: 'center'
                            }}
                            onLoad={(e) => {
                                console.log('Logo loaded successfully from:', e.target.src);
                            }}
                            onError={(e) => {
                                console.error('Failed to load logo from:', e.target.src);
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                    <h1 className="sidebar-title">نصّار</h1>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', marginTop: '0.5rem' }}>
                        للخدمات التجارية
                    </p>
                </div>

                <nav className="sidebar-nav">
                    <Link 
                        to="/dashboard/clearance-files" 
                        className={`nav-item ${isActive('/dashboard/clearance-files') ? 'active' : ''}`}
                        onClick={closeSidebar}
                    >
                        <svg className="nav-item-icon" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                        <span>متابعة التخليص</span>
                    </Link>

                    <Link 
                        to="/dashboard/shipment-tracking" 
                        className={`nav-item ${isActive('/dashboard/shipment-tracking') ? 'active' : ''}`}
                        onClick={closeSidebar}
                    >
                        <svg className="nav-item-icon" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                        </svg>
                        <span>متابعة الشحن</span>
                    </Link>

                    <Link 
                        to="/dashboard/customs-tariffs" 
                        className={`nav-item ${isActive('/dashboard/customs-tariffs') ? 'active' : ''}`}
                        onClick={closeSidebar}
                    >
                        <svg className="nav-item-icon" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1H8a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                        </svg>
                        <span>التعريفات الجمركية</span>
                    </Link>

                    <Link 
                        to="/dashboard/messages" 
                        className={`nav-item ${isActive('/dashboard/messages') ? 'active' : ''}`}
                        onClick={closeSidebar}
                    >
                        <svg className="nav-item-icon" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                        <span>نظام الرسائل</span>
                    </Link>

                    {user?.type === 'Admin' && (
                        <>
                            <Link 
                                to="/dashboard/activity-monitor" 
                                className={`nav-item ${isActive('/dashboard/activity-monitor') ? 'active' : ''}`}
                                onClick={closeSidebar}
                            >
                                <svg className="nav-item-icon" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                </svg>
                                <span>مراقبة الحركات</span>
                            </Link>
                            
                            <Link 
                                to="/dashboard/users" 
                                className={`nav-item ${isActive('/dashboard/users') ? 'active' : ''}`}
                                onClick={closeSidebar}
                            >
                                <svg className="nav-item-icon" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                </svg>
                                <span>المستخدمين</span>
                            </Link>
                        </>
                    )}
                </nav>
            </div>

            {/* Main Content */}
            <div className="main-content">
                {/* Header */}
                <header className="header">
                    <h2 className="header-title">
                        {isActive('/dashboard/clearance-files') && 'ملفات التخليص'}
                        {isActive('/dashboard/shipment-tracking') && 'متابعة الشحن'}
                        {isActive('/dashboard/customs-tariffs') && 'التعريفات الجمركية'}
                        {isActive('/dashboard/messages') && 'نظام الرسائل'}
                        {isActive('/dashboard/activity-monitor') && 'مراقبة الحركات'}
                        {isActive('/dashboard/users') && 'المستخدمين'}
                    </h2>

                    <div className="header-actions">
                        <div className="user-info">
                            <span className="user-name">{user?.name || 'Admin'}</span>
                            <div className="user-avatar">
                                {user?.name?.charAt(0)?.toUpperCase() || 'N'}
                            </div>
                        </div>

                        <button onClick={handleLogout} className="btn btn-outline">
                            <svg style={{ width: '15px', height: '15px' }} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                            </svg>
                            <span>تسجيل الخروج</span>
                        </button>
                    </div>
                </header>

                {/* Content Area */}
                <div className="content">
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default DashboardLayout;

